/*
 * OC Recruitment — Homepage
 * Design: "The Boardroom" Corporate Modernism — Boutique Retained Search Firm
 * - Midnight Blue (#0F2137) / Slate (#475569) / Steel Blue (#4A7C9B)
 * - DM Serif Display headlines / DM Sans body
 * - Communicates within seconds: (1) packaging specialist, (2) sales/commercial leadership, (3) retained search
 */

import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Search,
  Target,
  Users,
  Award,
  Briefcase,
  Building2,
  TrendingUp,
  Shield,
} from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import FAQ from "@/components/FAQ";
import { pageSEO, organizationSchema, websiteSchema } from "@/lib/seoConfig";

const homeFAQs = [
  {
    question: "What makes OC Recruitment different from other recruitment firms?",
    answer: "OC Recruitment focuses exclusively on sales and commercial leadership roles within the US packaging industry. Unlike generalist recruiters, we understand the difference between flexible film and rigid plastics, know the key players across every packaging segment, and can assess whether a candidate has the commercial acumen to drive growth in your specific market. This specialist focus, combined with 16+ years of dedicated packaging recruitment experience, means we deliver candidates that generalist firms simply cannot reach.",
  },
  {
    question: "What packaging sectors does OC Recruitment cover?",
    answer: "We recruit across the full US packaging value chain: flexible packaging, rigid plastics, corrugated packaging, folding carton, labels and sleeves, glass packaging, metal packaging, and caps and closures. Whether you are a converter, manufacturer, or distributor, we have deep networks within your specific segment.",
  },
  {
    question: "What types of sales roles do you recruit for in the packaging industry?",
    answer: "We recruit across the full spectrum of commercial roles — from individual contributors such as Business Development Managers, Sales Representatives, Account Managers, and Key Account Managers, through to senior leadership including Sales Directors, Vice Presidents of Sales, Senior Vice Presidents, Executive Vice Presidents, and Chief Commercial Officers.",
  },
  {
    question: "How does the retained search process work?",
    answer: "Each engagement begins with a detailed briefing to understand your business, culture, and the specific commercial challenges the role needs to address. We then conduct targeted headhunting and market mapping across your packaging segment, directly approaching proven performers who are typically not visible through job advertising. You receive a shortlist of thoroughly assessed candidates, with full transparency throughout the process.",
  },
  {
    question: "Why can't I just use a job board to hire packaging sales talent?",
    answer: "The strongest sales professionals in packaging are rarely looking for new roles. They are already performing well within competing manufacturers, converters, or distributors. Job boards only reach active job seekers — typically a fraction of the available talent pool. Retained search reaches the passive candidates who would never see your advertisement but would consider the right opportunity when approached directly and professionally.",
  },
  {
    question: "How long does a typical packaging sales recruitment assignment take?",
    answer: "Most retained search assignments are completed within 6 to 10 weeks, depending on the seniority of the role and the specificity of the packaging segment. Because we already have established networks across every major US packaging sector, we can often present strong initial candidates within the first two to three weeks.",
  },
];

const HERO_BG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/hero-packaging-products-jTcsMjjFhH8thZMMjwdh3M.webp";

/* 8 packaging sectors */
const sectors = [
  { name: "Rigid Plastics", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-rigid-mvMgsT67azHQXtquiyVzSN.webp", scale: "" },
  { name: "Flexible Packaging", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-flexible-cRxnuNze4ebgvqMYPQg5fX.webp", scale: "" },
  { name: "Corrugated Packaging", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-corrugated-v2-cx9aaRddU4AgpZMmZPwH7b.webp", scale: "" },
  { name: "Folding Carton", image: "/manus-storage/folding-carton-packaging-optimized_96338168.webp", scale: "" },
  { name: "Labels & Sleeves", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-labels-v2-UDnPqNi2SYX63T855gAvBY.webp", scale: "" },
  { name: "Glass Packaging", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-glass-io6hk96t4qCiuxoLB9GbdR.webp", scale: "" },
  { name: "Metal Packaging", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-metal-7KZGViCDGNpJzUE97Qvcdm.webp", scale: "" },
  { name: "Caps & Closures", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-closures-bAGnetrxDcpH3Kx4dsKNYZ.webp", scale: "" },
];

const individualContributors = [
  { title: "Business Development Managers", icon: TrendingUp },
  { title: "Sales Representatives", icon: Target },
  { title: "Account Managers", icon: Users },
  { title: "Key Account Managers", icon: Building2 },
];

const salesLeadership = [
  { title: "Sales Directors", icon: Briefcase },
  { title: "Vice Presidents of Sales", icon: Award },
  { title: "Senior Vice Presidents of Sales", icon: Shield },
  { title: "Executive Vice Presidents of Sales", icon: TrendingUp },
  { title: "Chief Commercial Officers", icon: Building2 },
];

const reasons = [
  {
    title: "16+ Years in Packaging",
    description:
      "Over sixteen years of dedicated search experience within the US packaging industry. This is not a sideline — packaging sales recruitment is our sole focus.",
    icon: Shield,
  },
  {
    title: "Deep Industry Network",
    description:
      "An established network of sales and commercial professionals across every major packaging segment, from flexible packaging and rigid plastics to folding carton, labels & sleeves, corrugated packaging, glass, metal and caps & closures.",
    icon: Users,
  },
  {
    title: "Specialist, Not Generalist",
    description:
      "We recruit exclusively for commercial roles in packaging. This specialism means we understand the market, the companies, and the talent better than any generalist firm.",
    icon: Target,
  },
  {
    title: "Proven Track Record",
    description:
      "A consistent history of placing high-performing sales and commercial leadership professionals who deliver results for packaging companies across the United States.",
    icon: TrendingUp,
  },
];

export default function Home() {
  return (
    <div>
      <SEO
        title={pageSEO.home.title}
        description={pageSEO.home.description}
        keywords={pageSEO.home.keywords}
        canonical={pageSEO.home.canonical}
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [organizationSchema, websiteSchema]
        }}
      />
      {/* ========== HERO ========== */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Premium packaging products including bottles, pouches, cartons, and closures"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2137]/92 via-[#0F2137]/80 to-[#0F2137]/55" />
        </div>

        <div className="container relative z-10 pt-32 pb-20">
          <div className="max-w-3xl">
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                Specialist Retained Search — US Packaging Industry
              </p>
            </motion.div>

            <motion.h1
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-6"
            >
              Your Next VP Sales Won't Come From a Job Board
            </motion.h1>

            <motion.p
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg sm:text-xl text-white/70 leading-relaxed max-w-2xl"
            >
              The best packaging sales talent isn't applying for roles — they're already succeeding at your competitors. OC Recruitment uses retained search to find, approach, and secure the high-performing commercial leaders that packaging companies across the US actually need.
            </motion.p>

            <motion.p
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="text-sm text-white/50 font-medium tracking-wide mb-10 max-w-2xl"
            >
              16+ years placing sales and commercial leadership professionals across every major US packaging sector.
            </motion.p>

            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                href="/for-companies"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all duration-200 group"
              >
                Hire Packaging Sales Talent
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
              <Link
                href="/for-candidates"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white text-sm font-semibold rounded hover:bg-white/10 transition-all duration-200"
              >
                Submit Your Resume
              </Link>
            </motion.div>
          </div>
        </div>

      </section>

      {/* ========== RETAINED SEARCH ========== */}
      <section className="pt-12 pb-20 lg:pt-16 lg:pb-28 bg-white">
        <div className="container">
          <div className="max-w-4xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                Our Approach
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-midnight mb-6">
                Retained Search for Packaging Sales
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="accent-border space-y-4">
                <p className="text-lg text-slate-brand leading-relaxed">
                  OC Recruitment partners with packaging manufacturers and
                  distributors on{" "}
                  <Link href="/for-companies" className="text-steel hover:underline">retained search assignments</Link>{" "}
                  to identify and secure high-impact sales and commercial leadership talent.
                </p>
                <p className="text-lg text-slate-brand leading-relaxed">
                  Each engagement combines targeted headhunting, detailed market
                  mapping, and direct outreach to proven commercial professionals
                  working within the{" "}
                  <Link href="/packaging-sectors" className="text-steel hover:underline">packaging industry</Link>.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-8 mt-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-midnight/5 flex items-center justify-center">
                    <Search size={18} className="text-steel" />
                  </div>
                  <span className="text-sm font-medium text-midnight">Targeted Headhunting</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-midnight/5 flex items-center justify-center">
                    <Target size={18} className="text-steel" />
                  </div>
                  <span className="text-sm font-medium text-midnight">Market Mapping</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-midnight/5 flex items-center justify-center">
                    <Users size={18} className="text-steel" />
                  </div>
                  <span className="text-sm font-medium text-midnight">Direct Outreach</span>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ========== WHY PACKAGING COMPANIES USE OC ========== */}
      <section className="py-20 lg:py-28 bg-pale">
        <div className="container">
          <div className="max-w-4xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                The Challenge
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-midnight mb-8">
                Why Packaging Companies Use OC Recruitment
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="accent-border space-y-4">
                <p className="text-lg text-slate-brand leading-relaxed">
                  Hiring proven sales professionals in the packaging industry is uniquely difficult.
                </p>
                <p className="text-lg text-slate-brand leading-relaxed">
                  The strongest performers rarely apply for roles. Most are already succeeding within competing manufacturers, converters, or packaging suppliers and must be approached directly through targeted headhunting.
                </p>
                <p className="text-lg text-slate-brand leading-relaxed">
                  OC Recruitment focuses exclusively on recruiting sales and commercial leadership professionals across the US packaging industry. This specialist focus provides access to a deep network of packaging professionals across flexible packaging, rigid plastics, labels, closures, corrugated, cartons, glass and metal packaging.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div className="mt-10">
                <p className="text-midnight font-semibold text-sm uppercase tracking-wider mb-5">
                  Typical Assignments Include
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-4 bg-white rounded border border-border">
                    <div className="w-9 h-9 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <Briefcase size={16} className="text-steel" />
                    </div>
                    <span className="text-sm font-medium text-midnight">Sales Directors leading regional or national commercial teams</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white rounded border border-border">
                    <div className="w-9 h-9 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <Award size={16} className="text-steel" />
                    </div>
                    <span className="text-sm font-medium text-midnight">Vice Presidents of Sales responsible for strategic growth</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white rounded border border-border">
                    <div className="w-9 h-9 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <TrendingUp size={16} className="text-steel" />
                    </div>
                    <span className="text-sm font-medium text-midnight">Business Development Managers opening new markets</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white rounded border border-border">
                    <div className="w-9 h-9 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <Users size={16} className="text-steel" />
                    </div>
                    <span className="text-sm font-medium text-midnight">National Account Managers managing major consumer packaged goods relationships</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.4}>
              <div className="accent-border mt-10">
                <p className="text-lg text-slate-brand leading-relaxed">
                  Through targeted headhunting and detailed market mapping, OC Recruitment identifies professionals already performing successfully within the packaging sector.
                </p>
                <p className="text-lg text-slate-brand leading-relaxed mt-4">
                  This specialist approach allows packaging companies to access high-quality candidates who are rarely visible through traditional job advertising.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.5}>
              <div className="mt-10">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-midnight text-white text-sm font-semibold rounded hover:bg-midnight/90 transition-all duration-200 group"
                >
                  Discuss Your Search
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ========== PACKAGING SECTORS ========== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <ScrollReveal>
            <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              Packaging Sectors
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl text-midnight mb-12">
              Across the Full US Packaging Value Chain
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-stretch">
            {sectors.map((sector, i) => (
              <ScrollReveal key={sector.name} delay={i * 0.05} className="h-full">
                <Link
                  href="/packaging-sectors"
                  className="group flex flex-col h-full bg-white rounded border border-border hover:border-steel/30 hover:shadow-md transition-all duration-300 overflow-hidden"
                >
                  <div className="relative h-28 sm:h-32 flex-shrink-0 overflow-hidden">
                    <img
                      src={sector.image}
                      alt={`${sector.name} packaging products`}
                      loading="lazy"
                      width={315}
                      height={235}
                      className={`w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ${sector.scale}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F2137]/50 to-transparent" />
                  </div>
                  <div className="p-3 text-center flex items-center justify-center min-h-[3.5rem]">
                    <span className="text-midnight font-medium text-xs sm:text-sm group-hover:text-steel transition-colors leading-tight">
                      {sector.name}
                    </span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.4}>
            <p className="text-slate-brand text-sm leading-relaxed mt-10 max-w-3xl">
              Recruiting commercial talent across the full US packaging value chain,
              from flexible packaging and rigid plastics to labels & sleeves, caps & closures,
              corrugated, glass and metal packaging.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ========== ROLES WE RECRUIT ========== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <ScrollReveal>
            <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              Roles We Recruit
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl text-midnight mb-6">
              From Individual Contributors to Executive Leadership
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-slate-brand leading-relaxed mb-12 max-w-3xl">
              We recruit across the full spectrum of commercial roles within
              packaging manufacturing and distribution, from individual
              contributors through to C-suite leadership.
            </p>
          </ScrollReveal>

          <div className="grid lg:grid-cols-2 gap-12">
            <ScrollReveal delay={0.1}>
              <div>
                <h3 className="text-lg text-midnight font-['DM_Serif_Display',Georgia,serif] mb-6 pb-3 border-b border-border">
                  Individual Contributors
                </h3>
                <div className="space-y-3">
                  {individualContributors.map((role) => (
                    <div
                      key={role.title}
                      className="flex items-center gap-4 p-4 bg-pale rounded border border-transparent hover:border-border transition-colors"
                    >
                      <div className="w-9 h-9 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                        <role.icon size={16} className="text-steel" />
                      </div>
                      <span className="text-sm font-medium text-midnight">
                        {role.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div>
                <h3 className="text-lg text-midnight font-['DM_Serif_Display',Georgia,serif] mb-6 pb-3 border-b border-border">
                  Sales Leadership
                </h3>
                <div className="space-y-3">
                  {salesLeadership.map((role) => (
                    <div
                      key={role.title}
                      className="flex items-center gap-4 p-4 bg-pale rounded border border-transparent hover:border-border transition-colors"
                    >
                      <div className="w-9 h-9 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                        <role.icon size={16} className="text-steel" />
                      </div>
                      <span className="text-sm font-medium text-midnight">
                        {role.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.3}>
            <div className="mt-10">
              <Link
                href="/case-studies"
                className="inline-flex items-center gap-2 text-midnight font-semibold text-sm hover:text-steel transition-colors group"
              >
                View Case Studies
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ========== WHY OC RECRUITMENT ========== */}
      <section className="py-20 lg:py-28 bg-midnight text-white">
        <div className="container">
          <ScrollReveal>
            <p className="text-white/70 text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              The OC Recruitment Difference
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl text-white mb-14">
              Why Packaging Companies Choose OC Recruitment
            </h2>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {reasons.map((reason, i) => (
              <ScrollReveal key={reason.title} delay={i * 0.1}>
                <div className="group">
                  <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center mb-5 group-hover:bg-steel/20 transition-colors">
                    <reason.icon size={22} className="text-steel" />
                  </div>
                  <h3 className="text-lg text-white mb-3 font-['DM_Serif_Display',Georgia,serif]">
                    {reason.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ABOUT OC RECRUITMENT ========== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                About the Firm
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-8">
                About OC Recruitment
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="accent-border space-y-4">
                <p className="text-slate-brand leading-relaxed">
                  OC Recruitment was founded by{" "}
                  <Link href="/about" className="text-steel hover:underline">Chris Kenny</Link>,
                  a specialist search professional with over 16 years of experience within
                  the packaging industry.
                </p>
                <p className="text-slate-brand leading-relaxed">
                  The firm focuses exclusively on{" "}
                  <Link href="/for-companies" className="text-steel hover:underline">retained search</Link>{" "}
                  for sales and commercial leadership professionals across the United States
                  packaging sector.
                </p>
                <p className="text-slate-brand leading-relaxed">
                  OC Recruitment partners with packaging manufacturers and
                  distributors to identify and secure the commercial talent that
                  drives growth. Explore our{" "}
                  <Link href="/case-studies" className="text-steel hover:underline">case studies</Link>{" "}
                  to see recent search assignments.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-10 mt-10 mb-8">
                <div>
                  <span className="text-3xl font-['DM_Serif_Display',Georgia,serif] text-midnight">
                    16+
                  </span>
                  <p className="text-xs text-slate-brand uppercase tracking-wider mt-1">
                    Years in Packaging
                  </p>
                </div>
                <div>
                  <span className="text-3xl font-['DM_Serif_Display',Georgia,serif] text-midnight">
                    8
                  </span>
                  <p className="text-xs text-slate-brand uppercase tracking-wider mt-1">
                    Packaging Sectors
                  </p>
                </div>
                <div>
                  <span className="text-3xl font-['DM_Serif_Display',Georgia,serif] text-midnight">
                    100%
                  </span>
                  <p className="text-xs text-slate-brand uppercase tracking-wider mt-1">
                    US Market Focus
                  </p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.4}>
              <Link
                href="/about"
                className="inline-flex items-center gap-2 text-midnight font-semibold text-sm hover:text-steel transition-colors group"
              >
                Learn More About OC Recruitment
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="Common Questions"
        items={homeFAQs}
        bgClass="bg-white"
      />

      {/* ========== FINAL CTA ========== */}
      <section className="py-20 lg:py-28 bg-pale">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-midnight mb-6">
                Strengthen Your Sales Team
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-lg text-slate-brand leading-relaxed mb-10">
                Whether you are a packaging company looking to secure
                high-performing commercial talent, or a sales professional
                exploring new opportunities in the industry, we are ready to
                have a conversation.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-midnight text-white text-sm font-semibold rounded hover:bg-midnight/90 transition-all duration-200 group"
                >
                  Hire Packaging Sales Talent
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
                <Link
                  href="/for-candidates"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-midnight/20 text-midnight text-sm font-semibold rounded hover:bg-midnight/5 transition-all duration-200"
                >
                  Submit Your Resume
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
